<script lang="ts" setup>
import GwCheckbox from '@/components/lib/GwCheckbox/GwCheckbox.vue'
import type { ID } from '@/types/basic'
import ColorNoteMenu from '@/components/lib/GwTable/ColorNote/ColorNoteMenu.vue'
import { updateHandColorMutation, updateHandNoteMutation } from '@/store/api/gql/hands/mutations'
import { ref } from 'vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import { useStore } from 'vuex'
import { t } from '@/locales/i18n'
import type { GwTableVariantConf } from './gwTableUtils'

const store = useStore()

export type GwTableRowProps = {
  isHoverable?: boolean
  hasCheckboxSpace?: boolean
  variantConf: GwTableVariantConf['row']
  active?: boolean
} & (
  | {
      // for tablerow selection all 7 props need to be present
      hasCheckbox: true
      hasNote: true
      hasColor: true
      note: string
      colorHex: string
      selectedItems: ID[]
      id: ID
    }
  | {
      hasCheckbox?: false
      hasNote?: false
      hasColor?: false
      note?: string
      colorHex?: string
      selectedItems?: undefined
      id?: ID
    }
)

const props = withDefaults(defineProps<GwTableRowProps>(), {
  isHoverable: true,
  selectedItems: () => [],
})

const emit = defineEmits(['click', 'click:checkbox'])

const l_colorHex = ref(props.colorHex || '')
const l_note = ref(props.note || '')

const handleColorUpdate = (colorHex: string) => {
  if (colorHex === l_colorHex.value) return
  l_colorHex.value = colorHex

  if (props.id) {
    updateHandColorMutation([props.id], colorHex)
      .then(() => {
        store.dispatch('messages', {
          type: 'success',
          text: t('hhv3.colorNote.hand_color_tag_updated'),
          hide: true,
        })
      })
      .catch(() => {
        l_colorHex.value = props.colorHex || ''
        store.dispatch('messages', {
          type: 'error',
          text: t('hhv3.colorNote.hand_color_tag_update_failed'),
          hide: true,
        })
      })
  }
}

const handleNoteUpdate = (note: string) => {
  if (note === l_note.value) return
  l_note.value = note
  if (props.id)
    updateHandNoteMutation([props.id], note)
      .then(() => {
        store.dispatch('messages', {
          type: 'success',
          text: t('hhv3.colorNote.hand_note_updated'),
          hide: true,
        })
      })
      .catch(() => {
        l_note.value = props.note || ''
        store.dispatch('messages', {
          type: 'error',
          text: t('hhv3.colorNote.hand_note_update_failed'),
          hide: true,
        })
      })
}
</script>

<template>
  <GwBox
    tag="tr"
    :variant="variantConf.tr.variant"
    :active
  >
    <td
      v-if="hasCheckbox || hasNote || hasColor"
      class="gwtable_td_sticky_left gwtable_td_checkbox d-flex align-center"
      style="height: var(--gwtable-row-height)"
    >
      <GwCheckbox
        v-if="hasCheckbox"
        data-tst="table_row_checkbox"
        style="--gwbtn-bg: var(--clr-back-td)"
        :selected="hasCheckbox && !!id && selectedItems.includes(id)"
        @change="emit('click:checkbox', id)"
      />
      <ColorNoteMenu
        :note="l_note"
        :colorHex="l_colorHex"
        @update="handleNoteUpdate"
        @update:color="handleColorUpdate"
      />
    </td>
    <slot></slot>
  </GwBox>
</template>
